export type ExperienceSiteType = "travel" | "car_rental" | "stays";

type ActiveExperiencePreview = {
  popup: Window;
  origin: string;
  context: { id: string; type: ExperienceSiteType; host: string; editorOrigin: string };
  draft: Record<string, unknown>;
  ready: boolean;
};

export type ExperiencePreviewAuthorization = {
  token: string;
  whatsappNumber?: string;
};

let activePreview: ActiveExperiencePreview | undefined;

const getExperienceSitesBaseUrl = () => {
  const configured = process.env.REACT_APP_EXPERIENCE_SITES_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === "localhost" || hostname === "127.0.0.1") return "http://localhost:3003";
  if (/(^|[.-])dev([.-]|$)/i.test(hostname)) return "https://sites-dev.lyproai.com";
  return "https://sites.lyproai.com";
};

export const openExperiencePreview = (
  experienceType: ExperienceSiteType,
  draft: Record<string, unknown>,
  hostname?: string,
  authorization?: ExperiencePreviewAuthorization,
) => {
  const config = draft.config && typeof draft.config === "object" ? draft.config as Record<string, unknown> : {};
  const previewHost = (hostname || String(config.primaryDomain || "preview.store.lyproai.com")).trim().toLowerCase();
  const previewDraft = {
    ...draft,
    ...(authorization ? {
      __lyproTravelPreviewToken: authorization.token,
      __lyproTravelPreviewWhatsappNumber: authorization.whatsappNumber || "",
    } : {}),
    config: { ...config, primaryDomain: previewHost },
  };
  const previewOrigin = getExperienceSitesBaseUrl();
  const editorOrigin = window.location.origin;

  if (activePreview && !activePreview.popup.closed && activePreview.context.type === experienceType && activePreview.origin === previewOrigin) {
    activePreview.context.host = previewHost;
    activePreview.context.editorOrigin = editorOrigin;
    activePreview.draft = previewDraft;
    if (activePreview.ready) {
      activePreview.popup.postMessage({
        type: "LYPRO_EXPERIENCE_PREVIEW_DRAFT",
        id: activePreview.context.id,
        experienceType: activePreview.context.type,
        host: activePreview.context.host,
        editorOrigin: activePreview.context.editorOrigin,
        draft: previewDraft,
      }, previewOrigin);
    }
    activePreview.popup.focus();
    return true;
  }

  const id = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const context = { id, type: experienceType, host: previewHost, editorOrigin };
  const targetUrl = new URL("/", previewOrigin);
  targetUrl.searchParams.set("__previewId", id);
  targetUrl.searchParams.set("__previewType", experienceType);
  targetUrl.searchParams.set("__previewHost", previewHost);
  targetUrl.searchParams.set("__previewOrigin", editorOrigin);
  const targetOrigin = targetUrl.origin;
  let popup: Window | null = null;
  let timer: number | undefined;
  const receiveReady = (event: MessageEvent) => {
    if (!popup || event.source !== popup || event.origin !== targetOrigin) return;
    if (event.data?.type !== "LYPRO_EXPERIENCE_PREVIEW_READY") return;
    if (event.data?.previewId && event.data.previewId !== id) return;
    if (activePreview?.popup === popup) activePreview.ready = true;
    popup.postMessage({
      type: "LYPRO_EXPERIENCE_PREVIEW_DRAFT",
      id: context.id,
      experienceType: context.type,
      host: context.host,
      editorOrigin: context.editorOrigin,
      draft: activePreview?.popup === popup ? activePreview.draft : previewDraft,
    }, targetOrigin);
    window.removeEventListener("message", receiveReady);
    if (timer) window.clearInterval(timer);
  };

  window.addEventListener("message", receiveReady);
  popup = window.open(targetUrl.toString(), "_blank");
  if (!popup) {
    window.removeEventListener("message", receiveReady);
    return false;
  }
  activePreview = { popup, origin: targetOrigin, context, draft: previewDraft, ready: false };
  timer = window.setInterval(() => {
    if (popup?.closed) {
      window.removeEventListener("message", receiveReady);
      if (timer) window.clearInterval(timer);
      if (activePreview?.popup === popup) activePreview = undefined;
    }
  }, 500);
  return true;
};
